'use client'

import { ProductCard } from '@/components/common'
import { ProductCardSkeleton } from '@/components/common/product-card/product-item-skeleton'
import { Button } from '@/components/ui/button'
import { useCollectionProductsNoFilter } from '@/hooks/react-query/products/use-collection-products'
import { cn } from '@/lib/utils'
import { TGenderType } from '@/types/product'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
// import { useEffect } from 'react'
// import { useCollectionFilterStore } from '../store/collection-store'

type Props = {
  slug: string
  className?: string
  filter?: { key: string; value: string | number }[]
  flatten?: number
  visibility?: boolean
  limit?: number
  hide_banner?: boolean
  show_variant_color?: boolean
  random_focus?: boolean
  hideTotalProducts?: boolean
  isHideId?: boolean
  genderType?: TGenderType
  sort?: string
}

export const CollectionListingNoFilter = ({
  slug,
  className,
  filter,
  flatten,
  visibility,
  limit,
  hide_banner,
  show_variant_color,
  random_focus,
  hideTotalProducts,
  isHideId,
  genderType,
  sort,
}: Props) => {
  // const setProductCount = useCollectionFilterStore(
  //   (s) => s.setProductCount
  // )
  const t = useTranslations()

  // Convert filter array to FilterValues object
  const filters = Object.fromEntries(
    (filter || []).map((f) => [f.key, f.value])
  )

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useCollectionProductsNoFilter(
      {
        slug,
        limit: limit ?? 12,
        hide_banner: hide_banner ?? true,
        show_variant_color: show_variant_color ?? false,
        random_focus: random_focus ?? false,
        flatten: flatten ?? 0,
        visibility: visibility,
        gender_type: genderType,
        sort,
      },
      filters
    )

  const handleLoadMore = async () => {
    await fetchNextPage()
  }

  // useEffect(() => {
  //   if (data) {
  //     setProductCount(data?.pages[0]?.total || 0)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [data])

  const allProducts = data?.pages?.flatMap((page) => page?.products || []) ?? []

  const totalProducts = data?.pages[0]?.pagination?.total || 0
  const currentShowing = allProducts.length

  if (isLoading) {
    return (
      <div className='w-full flex-1' id={!isHideId ? 'collection-listing' : ''}>
        <div
          className={cn(
            'grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5',
            className
          )}
        >
          {Array.from({ length: 8 }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='w-full flex-1' id={!isHideId ? 'collection-listing' : ''}>
      <div
        className={cn(
          'grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5',
          className
        )}
      >
        {allProducts.map((product, index) => (
          <ProductCard product={product} key={`${index}`} />
        ))}
        {/* Fallback to fake products if no data */}
        {allProducts.length === 0 && (
          <div className='col-span-full'>
            <p className='no-product-found text-center text-sm text-neutral-900/60'>
              {t('collection_page.message.no_product_found')}
            </p>
          </div>
        )}
        {isFetchingNextPage && (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <ProductCardSkeleton key={`loading-${index}`} />
            ))}
          </>
        )}
      </div>
      {hasNextPage && (
        <div className='mt-10 text-center lg:mt-17'>
          <Button
            variant='black'
            className='group'
            onClick={handleLoadMore}
            disabled={isFetchingNextPage}
          >
            {t('button.view_more')}
            <Image
              src='/icons/icon-arrow-line-right.svg'
              width={16}
              height={19}
              alt='icon arrow right'
              className='mb-1 h-5 w-4 transition-transform duration-200 group-hover:translate-x-1'
            />
          </Button>
          {!hideTotalProducts && (
            <p className='mt-1 font-sans text-sm leading-4.5 text-neutral-900/60 lg:mt-1.5 lg:text-base lg:leading-5'>
              {t('collection_page.message.display_product_count', {
                currentShowing,
                totalProducts,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
